import React, { useState } from 'react';
import { ChevronDown, FileText, Edit } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { TimelineEvent } from '../../../models/TimelineEvent';
import { getEventTypeIcon, getEventTypeColor } from './event-type-config';
import EventEditForm from './event-edit-form';
import DocumentViewerDialog from './document-viewer-dialog';
import { formatCompactDateTime } from '../../../utils/timeline-date-utils';
import { useAppSelector } from '../../../redux/store';
import { selectCanEditCurrentCase } from '../../../redux/selectors/knowledgebaseSelectors';

interface TimelineEventCardProps {
  event: TimelineEvent;
  isLast?: boolean;
}

export default function TimelineEventCard({ event, isLast = false }: TimelineEventCardProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Check if user can edit
  const canEdit = useAppSelector(selectCanEditCurrentCase);

  const handleCardClick = () => {
    setShowDocumentViewer(true);
  };

  const EventIcon = getEventTypeIcon(event.eventType);
  const eventColor = getEventTypeColor(event.eventType);

  const sourceCount = event.sources?.length || (event.sourceText ? 1 : 0);

  return (
    <div
      className="flex relative group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Timeline line - single-sided, thinner, on far left */}
      {!isLast && (
        <div className="absolute left-4 top-8 bottom-0 w-px bg-border" />
      )}

      {/* Icon circle - smaller and compact */}
      <div
        className="min-w-8 h-8 rounded-full flex items-center justify-center mr-3 mt-0.5 z-10 flex-shrink-0"
        style={{ backgroundColor: eventColor }}
      >
        <EventIcon className="h-4 w-4 text-white" />
      </div>

      {/* Event card - compact design */}
      <Card
        className="flex-1 cursor-pointer transition-all duration-200 hover:border-gold/50 border border-border rounded-md"
        onClick={handleCardClick}
      >
        <CardContent className="p-2.5">
          <Collapsible open={expanded} onOpenChange={setExpanded}>
            {/* Header - compact */}
            <div className="flex justify-between items-start mb-1.5 gap-2">
              <div className="flex-1 min-w-0">
                {/* Compact date above title */}
                <p className="text-[10px] text-muted-foreground mb-0.5">
                  {formatCompactDateTime(event.startDate, event.startTime)}
                </p>
                <h4 className="font-semibold text-xs mb-1 line-clamp-2">
                  {event.title}
                  {event.isUserModified && (
                    <Badge variant="secondary" className="ml-1.5 text-[10px] h-4 px-1.5 bg-gold/10 text-gold border-gold/20">
                      {t('common.timeline.userModified')}
                    </Badge>
                  )}
                </h4>
              </div>
              {/* Actions - show on hover, only show Edit button for editors */}
              <div className={`flex gap-0.5 transition-opacity duration-200 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
                {canEdit && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowEditDialog(true);
                    }}
                  >
                    <Edit className="h-3.5 w-3.5" />
                  </Button>
                )}
                <CollapsibleTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ChevronDown
                      className={`h-3.5 w-3.5 transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`}
                    />
                  </Button>
                </CollapsibleTrigger>
              </div>
            </div>

            {/* Event type badge - smaller */}
            <Badge
              className="mb-1.5 text-[10px] h-4 px-1.5"
              style={{ backgroundColor: eventColor, color: 'white' }}
            >
              {t(`common.timeline.eventTypes.${event.eventType}`)}
            </Badge>

            {/* Description preview - compact */}
            <p
              className={`text-xs text-muted-foreground ${expanded ? '' : 'line-clamp-2'
                } ${expanded ? 'mb-2' : ''}`}
            >
              {event.description}
            </p>

            {/* Expanded content */}
            <CollapsibleContent>
              <div className="mt-2.5 pt-2.5 border-t border-border/50">
                {/* Parties involved */}
                {event.partiesInvolved && event.partiesInvolved.length > 0 && (
                  <div className="mb-2">
                    <p className="text-[10px] font-semibold mb-1">
                      {t('common.timeline.partiesInvolved')}:
                    </p>
                    <div className="flex gap-1 flex-wrap">
                      {event.partiesInvolved.map((party, idx) => (
                        <Badge key={idx} variant="outline" className="text-[10px] h-4 px-1.5">
                          {party}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Legal significance */}
                {event.legalSignificance && (
                  <div className="mb-1.5">
                    <span className="text-[10px] font-semibold">
                      {t('common.timeline.significance')}:{' '}
                    </span>
                    <Badge
                      variant={
                        event.legalSignificance === 'high' ? 'destructive' :
                          event.legalSignificance === 'medium' ? 'default' : 'secondary'
                      }
                      className="text-[10px] h-4 px-1.5"
                    >
                      {event.legalSignificance}
                    </Badge>
                  </div>
                )}

                {/* Confidence score */}
                <p className="text-[10px] text-muted-foreground mb-1.5">
                  {t('common.timeline.confidence')}: {Math.round(event.confidenceScore * 100)}%
                </p>

                {/* Source indicator */}
                {sourceCount > 0 && (
                  <div
                    className="mt-1.5 flex items-center gap-1 cursor-pointer hover:opacity-80"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDocumentViewer(true);
                    }}
                  >
                    <FileText className="h-3 w-3 text-muted-foreground" />
                    <p className="text-[10px] text-muted-foreground hover:underline">
                      {sourceCount > 1
                        ? t('common.timeline.documentViewer.viewSources', { count: sourceCount })
                        : t('common.timeline.documentViewer.viewSource')
                      }
                    </p>
                  </div>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
            <DialogTitle>{t('common.timeline.editEvent.title')}</DialogTitle>
            <DialogDescription>
              {t('common.timeline.editEventDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 min-h-0">
            <EventEditForm
              event={event}
              onCancel={() => setShowEditDialog(false)}
              onSave={() => {
                setShowEditDialog(false);
                // The Redux store will automatically update the event
              }}
              onDelete={() => {
                setShowEditDialog(false);
              }}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Document Viewer Dialog */}
      <DocumentViewerDialog
        event={event}
        open={showDocumentViewer}
        onOpenChange={setShowDocumentViewer}
      />
    </div>
  );
}
